import type { User } from '../types/auth';
import { isSnapfyInternal } from './snapfyInternal';

/**
 * Segmentos da Snapfy. "fashion" é a plataforma de sempre; "marketing" é a
 * plataforma de anúncios (Marketing Studio), com menu e fluxo próprios.
 * O segmento é derivado da URL — tudo sob /marketing é Marketing — então link
 * direto, reload e voltar do navegador sempre mostram o menu certo. O
 * /marketing-studio antigo continua no Fashion até ser migrado para dentro do
 * segmento (hoje ele não tem item no menu Marketing).
 */
export type AppSegment = 'fashion' | 'marketing';

/**
 * Início do Product Studio (segmento Fashion) — a antiga home "/". A raiz
 * snapfy.ai agora é a landing pública; todo "voltar para o início" do app
 * aponta para cá.
 */
export const APP_HOME = '/product-studio';

export const SEGMENT_HOME: Record<AppSegment, string> = {
  fashion: APP_HOME,
  marketing: '/marketing',
};

export const getSegment = (pathname: string): AppSegment =>
  /^\/marketing(?:\/|$)/.test(pathname) ? 'marketing' : 'fashion';

/**
 * Quem vê o seletor de segmentos e entra em /marketing/*. Por padrão só a equipe
 * Snapfy (@snapfy.ai); com `VITE_MARKETING_PUBLIC=true` no build, também
 * qualquer cliente com e-mail verificado. Lido na chamada (não no load do módulo) para os testes
 * poderem trocar a env.
 */
export const canUseMarketingSegment = (user: User | null | undefined): boolean => {
  if (isSnapfyInternal(user)) return true;
  // Clientes só com e-mail verificado — mesma regra do MarketingSegmentOnly da API.
  return import.meta.env.VITE_MARKETING_PUBLIC === 'true' && !!user?.emailVerifiedAt;
};

const storageKey = (segment: AppSegment) => `snapfy_segment_last_${segment}`;

/** Última rota visitada em cada segmento, para o seletor voltar de onde parou. */
export const rememberSegmentPath = (segment: AppSegment, path: string): void => {
  try {
    localStorage.setItem(storageKey(segment), path);
  } catch {
    /* storage indisponível: o seletor cai na home do segmento */
  }
};

export const lastSegmentPath = (segment: AppSegment): string => {
  try {
    const saved = localStorage.getItem(storageKey(segment));
    // "/" salvo antes da landing apontava para a antiga home do Fashion — hoje é a landing.
    const savedPath = saved?.split('?')[0];
    if (saved && savedPath !== '/' && getSegment(savedPath as string) === segment) return saved;
  } catch {
    /* storage indisponível */
  }
  return SEGMENT_HOME[segment];
};

/**
 * Apps do Product Studio que também abrem dentro do Marketing (menu único).
 * Rota do Product Studio → rota no segmento Marketing; a mais longa primeiro.
 */
const STUDIO_IN_MARKETING: [string, string][] = [
  ['/models/create', '/marketing/modelos/criar'],
  ['/models', '/marketing/modelos'],
  ['/moodboard', '/marketing/canvas'],
  ['/workflow', '/marketing/workflow'],
  ['/batch', '/marketing/lookbook'],
  ['/brandbook', '/marketing/brandbook'],
  ['/gallery', '/marketing/galeria'],
];

/**
 * Link entre apps do Product Studio: quem está no Marketing continua nele
 * (`/moodboard?edit=1` vira `/marketing/canvas?edit=1`); no Product Studio, o
 * caminho volta como veio.
 */
export const studioPath = (path: string, currentPathname: string): string => {
  if (getSegment(currentPathname) !== 'marketing') return path;
  const cut = path.search(/[?#]/);
  const pathname = cut === -1 ? path : path.slice(0, cut);
  const match = STUDIO_IN_MARKETING.find(([studio]) => pathname === studio);
  return match ? match[1] + path.slice(pathname.length) : path;
};
